import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Message } from '../types';

export interface ChatUser {
    id: string;
    name: string;
    avatar_url?: string | null;
}

export interface RealtimeChatMessage {
    id: string;
    content: string;
    user: ChatUser;
    createdAt: string;
}

interface UseRealtimeChatOptions {
    roomName: string; // chat_id
    currentUser: ChatUser;
    enabled?: boolean;
}

interface UseRealtimeChatReturn {
    messages: RealtimeChatMessage[];
    sendMessage: (content: string) => Promise<void>;
    isConnected: boolean;
    isLoading: boolean;
}

export function useRealtimeChat({
    roomName,
    currentUser,
    enabled = true
}: UseRealtimeChatOptions): UseRealtimeChatReturn {
    const [messages, setMessages] = useState<RealtimeChatMessage[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

    // Fetch initial messages from DB
    useEffect(() => {
        if (!enabled || !roomName) {
            setIsLoading(false);
            return;
        }

        const fetchMessages = async () => {
            setIsLoading(true);
            const { data, error } = await supabase
                .from('messages')
                .select(`
                    *,
                    sender:profiles(id, email)
                `)
                .eq('chat_id', roomName)
                .order('created_at', { ascending: true });

            if (error) {
                console.error('Error fetching messages:', error);
                setIsLoading(false);
                return;
            }

            const formattedMessages: RealtimeChatMessage[] = (data || []).map((msg: Message & { sender: any }) => ({
                id: msg.id,
                content: msg.content,
                user: {
                    id: msg.sender_id,
                    name: msg.sender?.email || 'Unknown',
                    avatar_url: undefined
                },
                createdAt: msg.created_at
            }));

            setMessages(formattedMessages);
            setIsLoading(false);
        };

        fetchMessages();
    }, [roomName, enabled]);

    // Subscribe to Realtime Postgres Changes
    useEffect(() => {
        if (!enabled || !roomName) return;

        const channel = supabase.channel(`chat:${roomName}`);

        channel
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'messages',
                    filter: `chat_id=eq.${roomName}`
                },
                async (payload) => {
                    const newRecord = payload.new as Message;
                    
                    // Prevent duplicates from optimistic updates
                    // We check if we already have a message with this ID
                    setMessages(prev => {
                        if (prev.some(m => m.id === newRecord.id)) {
                            return prev;
                        }
                        return prev; // Return prev here to avoid state update if duplicate, but we need to handle "new" messages differently if they aren't in state
                    });

                    // If it's NOT in our state (incoming message from someone else, or a missed optimistic update), fetch sender and add it
                    // We check state again inside a separate functional update or just proceed? 
                    // The safer way is to fetch sender info first.
                    
                    let senderInfo: ChatUser = { id: newRecord.sender_id, name: 'Unknown', avatar_url: null };

                    // If it's me (should be caught by optimistic, but just in case)
                    if (newRecord.sender_id === currentUser.id) {
                         // We assume optimistic update handled it, but if not (e.g. another tab), usage of currentUser is correct.
                         senderInfo = { ...currentUser, avatar_url: currentUser.avatar_url || null };
                    } else {
                        // Fetch sender profile
                        const { data: profile } = await supabase
                            .from('profiles')
                            .select('email')
                            .eq('id', newRecord.sender_id)
                            .single();
                        
                        if (profile) {
                            senderInfo = {
                                id: newRecord.sender_id,
                                name: profile.email || 'User',
                                avatar_url: undefined
                            };
                        }
                    }

                    const newMessage: RealtimeChatMessage = {
                        id: newRecord.id,
                        content: newRecord.content,
                        user: senderInfo,
                        createdAt: newRecord.created_at
                    };

                    setMessages(prev => {
                        if (prev.some(m => m.id === newMessage.id)) return prev;
                        return [...prev, newMessage];
                    });
                }
            )
            .subscribe((status) => {
                setIsConnected(status === 'SUBSCRIBED');
            });

        channelRef.current = channel;

        return () => {
            supabase.removeChannel(channel);
            channelRef.current = null;
            setIsConnected(false);
        };
    }, [roomName, enabled, currentUser]);

    // Send message function
    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim() || !roomName || !currentUser) return;

        const messageId = crypto.randomUUID();
        const now = new Date().toISOString();

        const newMessage: RealtimeChatMessage = {
            id: messageId,
            content: content.trim(),
            user: currentUser,
            createdAt: now
        };

        // Optimistic update
        setMessages(prev => [...prev, newMessage]);

        // We DO NOT broadcast anymore. We insert to DB, and the subscription picks it up (or ignores if deduped).
        // This ensures the DB is the single source of truth.

        // Persist to database
        const { error } = await supabase.from('messages').insert({
            id: messageId,
            chat_id: roomName,
            sender_id: currentUser.id,
            content: content.trim(),
            created_at: now
        });

        if (error) {
            console.error('Error saving message:', error);
            // Rollback optimistic update on error
            setMessages(prev => prev.filter(m => m.id !== messageId));
        }
    }, [roomName, currentUser]);

    return {
        messages,
        sendMessage,
        isConnected,
        isLoading
    };
}
